import { create } from 'zustand';
import defaultConfig from '../config/defaultConfig.json';
import { loadStored, saveStored, clearStored, deepEqual } from '../utils/storage';

const stored = loadStored();

function mergeWithDefault(input) {
  if (!input || typeof input !== 'object') return structuredClone(defaultConfig);
  const settings = { ...defaultConfig.settings, ...(input.settings || {}) };
  settings.editor = { ...defaultConfig.settings.editor, ...((input.settings || {}).editor || {}) };
  return {
    version: defaultConfig.version,
    settings,
    ui: { ...defaultConfig.ui, ...(input.ui || {}) },
    history: { ...defaultConfig.history, ...(input.history || {}) },
  };
}

export const useConfig = create((set, get) => ({
  config: mergeWithDefault(stored),
  isDifferentFromDefault: stored ? !deepEqual(stored, defaultConfig) : false,
  promptMismatch: stored ? !deepEqual(mergeWithDefault(stored), defaultConfig) : false,

  set: (patch) => {
    const next = typeof patch === 'function' ? patch(get().config) : { ...get().config, ...patch };
    set({ config: next, isDifferentFromDefault: !deepEqual(next, defaultConfig) });
    if (next.settings?.autoSave !== false) saveStored(next);
  },

  setPath: (path, value) => {
    const cfg = structuredClone(get().config);
    const keys = path.split('.');
    let cur = cfg;
    for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
    cur[keys.at(-1)] = value;
    set({ config: cfg, isDifferentFromDefault: !deepEqual(cfg, defaultConfig) });
    if (cfg.settings?.autoSave !== false) saveStored(cfg);
  },

  replace: (next) => {
    const merged = mergeWithDefault(next);
    set({ config: merged, isDifferentFromDefault: !deepEqual(merged, defaultConfig), promptMismatch: false });
    saveStored(merged);
  },

  resetDefault: () => {
    const fresh = structuredClone(defaultConfig);
    set({ config: fresh, isDifferentFromDefault: false, promptMismatch: false });
    saveStored(fresh);
  },

  wipe: () => {
    clearStored();
    const fresh = structuredClone(defaultConfig);
    set({ config: fresh, isDifferentFromDefault: false, promptMismatch: false });
  },

  dismissMismatch: () => set({ promptMismatch: false }),
}));

export { defaultConfig };
